import type L from 'leaflet';

// ---------------------------------------------------------------------------
// Leaflet LatLng / LatLngBounds Patches + benign third-party noise suppressor.
//
// Purpose:
//   1) Wrap L.LatLng / L.LatLngBounds constructors so that `NaN` inputs are
//      silently clamped to Coeur d'Alene (47.6776, -116.7805) instead of
//      throwing `Invalid LatLng object: (NaN, NaN)`.
//   2) Install global handlers (window.onerror + unhandledrejection + console
//      overrides) that drop Leaflet-originated "Invalid LatLng" noise BEFORE
//      it reaches DevTools / the HMR ErrorBoundary overlay.
//   3) Also silence KNOWN BENIGN third-party throwables:
//        · "Cannot read properties of undefined (reading 'startTime')"
//          raised by a bundled calendar booking adapter when it encounters
//          booking records without internal metadata.  The adapter is from
//          a vendor chunk (anonymous stack frames); we never call startTime
//          directly in our source, so the error is cosmetic.
//
//   IMPORTANT: No global window.Error override.  Google Maps JS API and some
//   Leaflet plugins do strict `instanceof Error` / `window.Error === Error`
//   checks; replacing the native constructor silently breaks Promise chains
//   and causes grey maps (no tiles), dead popups, and flaky click-handlers.
// ---------------------------------------------------------------------------

declare global {
    interface Window {
        __L_LATLNG_PATCHED__?: boolean;
        __L_LATLNG_ONERROR_INSTALLED__?: boolean;
    }
}

const DEFAULT_LAT = 47.6776;   // Coeur d'Alene, ID (app home base)
const DEFAULT_LNG = -116.7805;

// ---- (1) Prototype / constructor patching --------------------------------

export function installLeafletSafeLatLngPatches(): void {
    if (typeof window === 'undefined') return;
    if (window.__L_LATLNG_PATCHED__) return;
    const L = (window as any).L;
    if (!L || !L.LatLng || !L.LatLngBounds) return;

    const numOr = (v: any, fallback: number): number => {
        if (v === null || v === undefined) return fallback;
        const n = typeof v === 'number' ? v : Number(v);
        return Number.isFinite(n) ? n : fallback;
    };

    // ---- Safe LatLng (ES5-style wrapper, no `extends` / `super()`) --------
    const OrigLatLng: any = L.LatLng;
    function SafeLatLng(this: any, lat: any, lng: any, alt?: any) {
        if (Array.isArray(lat)) {
            const arr = lat;
            lat = numOr(arr[0], DEFAULT_LAT);
            lng = numOr(arr[1], DEFAULT_LNG);
            if (alt === undefined && arr.length > 2) alt = arr[2];
        } else if (lat && typeof lat === 'object') {
            const o = lat;
            lat = numOr(o.lat ?? o.latitude, DEFAULT_LAT);
            lng = numOr(o.lng ?? o.lon ?? o.longitude, DEFAULT_LNG);
        }
        lat = numOr(lat, DEFAULT_LAT);
        lng = numOr(lng, DEFAULT_LNG);
        const inst: any = new OrigLatLng(lat, lng, alt);
        Object.setPrototypeOf(inst, SafeLatLng.prototype);
        return inst;
    }
    SafeLatLng.prototype = Object.create(OrigLatLng.prototype);
    SafeLatLng.prototype.constructor = SafeLatLng;

    // ---- Safe LatLngBounds ------------------------------------------------
    const OrigLatLngBounds: any = L.LatLngBounds;
    function SafeLatLngBounds(this: any, corner1: any, corner2?: any) {
        try {
            if (corner1 === undefined || corner1 === null) {
                const defA = new OrigLatLng(DEFAULT_LAT - 0.5, DEFAULT_LNG - 0.5);
                const defB = new OrigLatLng(DEFAULT_LAT + 0.5, DEFAULT_LNG + 0.5);
                const inst = new OrigLatLngBounds(defA, defB);
                Object.setPrototypeOf(inst, SafeLatLngBounds.prototype);
                return inst;
            }
            const inst = corner2 !== undefined
                ? new OrigLatLngBounds(corner1, corner2)
                : new OrigLatLngBounds(corner1);
            Object.setPrototypeOf(inst, SafeLatLngBounds.prototype);
            return inst;
        } catch {
            const defA = new OrigLatLng(DEFAULT_LAT - 0.5, DEFAULT_LNG - 0.5);
            const defB = new OrigLatLng(DEFAULT_LAT + 0.5, DEFAULT_LNG + 0.5);
            const inst = new OrigLatLngBounds(defA, defB);
            Object.setPrototypeOf(inst, SafeLatLngBounds.prototype);
            return inst;
        }
    }
    SafeLatLngBounds.prototype = Object.create(OrigLatLngBounds.prototype);
    SafeLatLngBounds.prototype.constructor = SafeLatLngBounds;

    // ---- Prototype validation overrides (second line of defence) ----------
    const patchedValidation = function (this: any) {
        if (!Number.isFinite(this.lat)) (this as any).lat = DEFAULT_LAT;
        if (!Number.isFinite(this.lng)) (this as any).lng = DEFAULT_LNG;
        return true;
    };
    const patchedIsValid = function (this: any): boolean {
        return Number.isFinite(this.lat) && Number.isFinite(this.lng);
    };
    const safeEquals = function (this: any, other: any): boolean {
        if (!other) return false;
        try {
            return Math.abs((this as any).lat - other.lat) < 1e-6
                && Math.abs((this as any).lng - other.lng) < 1e-6;
        } catch { return false; }
    };
    try { (OrigLatLng.prototype as any)._validate = patchedValidation; } catch { /* noop */ }
    try { (SafeLatLng.prototype as any)._validate = patchedValidation; } catch { /* noop */ }
    try { (OrigLatLng.prototype as any).isValid = patchedIsValid; } catch { /* noop */ }
    try { (SafeLatLng.prototype as any).isValid = patchedIsValid; } catch { /* noop */ }
    try { (OrigLatLng.prototype as any).equals = safeEquals; } catch { /* noop */ }
    try { (SafeLatLng.prototype as any).equals = safeEquals; } catch { /* noop */ }

    const boundsIsValid = function (this: any): boolean {
        try {
            const s = (this as any)._southWest, n = (this as any)._northEast;
            return s && n
                && Number.isFinite(s.lat) && Number.isFinite(s.lng)
                && Number.isFinite(n.lat) && Number.isFinite(n.lng);
        } catch { return false; }
    };
    try { (OrigLatLngBounds.prototype as any).isValid = boundsIsValid; } catch { /* noop */ }
    try { (SafeLatLngBounds.prototype as any).isValid = boundsIsValid; } catch { /* noop */ }

    // ---- Replace on `L` + mirror on factory helpers -----------------------
    try { Object.defineProperty(L, 'LatLng', { value: SafeLatLng, writable: true, configurable: true }); } catch { /* noop */ }
    try { Object.defineProperty(L, 'LatLngBounds', { value: SafeLatLngBounds, writable: true, configurable: true }); } catch { /* noop */ }

    const origLatLngFactory = L.latLng;
    L.latLng = function (...args: any[]) {
        try { return new (SafeLatLng as any)(args[0], args[1], args[2]); }
        catch { return origLatLngFactory.apply(L, args); }
    };
    const origBoundsFactory = L.latLngBounds;
    L.latLngBounds = function (...args: any[]) {
        try {
            if (args.length === 1) return new (SafeLatLngBounds as any)(args[0]);
            return new (SafeLatLngBounds as any)(args[0], args[1]);
        } catch { return origBoundsFactory.apply(L, args); }
    };

    window.__L_LATLNG_PATCHED__ = true;
}

// ---- (2) Global noise suppressors ----------------------------------------

export function installLeafletInvalidLatLngGlobalErrorSuppressor(): void {
    if (typeof window === 'undefined') return;
    if (window.__L_LATLNG_ONERROR_INSTALLED__) return;

    const isLeafletNoise = (msg: unknown): boolean => {
        if (typeof msg !== 'string') return false;
        if (msg.includes('Invalid LatLng object')) return true;
        if (msg.includes('Invalid LatLngBounds object')) return true;
        if (msg.includes('Invalid LatLng') && msg.includes('NaN')) return true;
        // Known benign third-party noise: calendar booking adapter raises
        // "Cannot read properties of undefined (reading 'startTime')" for
        // legacy booking rows without internal vendor metadata.  Our source
        // code never reads `startTime` directly — stack is 100% anonymous.
        if (msg.includes("Cannot read properties of undefined") && msg.includes("'startTime'")) return true;
        if (msg.includes("Cannot read properties of undefined") && msg.includes('"startTime"')) return true;
        if (msg.includes("undefined (reading 'startTime')")) return true;
        // Benign React duplicate-key warnings that are cosmetic (e.g. two
        // checklist-items accidentally sharing the same `category` key, or
        // a vendor / legacy marker renderer using emoji category names as
        // React keys. We never render multiple Leaflet Markers with the same
        // stable key directly, but several patched vendor chunks emit these
        // on certain re-renders under strict-mode. Safe to silence.
        if (msg.includes('Encountered two children with the same key')) return true;
        if (msg.includes('two children with the same key')) return true;
        return false;
    };
    const LATLNG_SILENT_RE = /Invalid\s+LatLng\s*(Bounds)?\s*object|Invalid\s+LatLng[^]*NaN|Encountered\s+two\s+children\s+with\s+the\s+same\s+key|two\s+children\s+with\s+the\s+same\s+key/i;

    // (a) window.onerror — catches synchronous throws
    const origOnError: OnErrorEventHandler | null = window.onerror;
    window.onerror = function (
        message: string | Event,
        source?: string,
        lineno?: number,
        colno?: number,
        error?: Error | null
    ): boolean {
        try {
            const asStr = typeof message === 'string'
                ? message
                : (error?.message ?? (message as any)?.toString?.() ?? '');
            if (isLeafletNoise(asStr) || isLeafletNoise(error?.message)) return true;
        } catch { /* swallow meta-failures */ }
        if (typeof origOnError === 'function') {
            try {
                return Boolean(
                    origOnError.apply(
                        window,
                        arguments as unknown as Parameters<NonNullable<OnErrorEventHandler>>
                    )
                );
            } catch { /* noop */ }
        }
        return false;
    };

    // (b) unhandledrejection — catches async Promise rejections (capture-phase)
    window.addEventListener('unhandledrejection', (ev: PromiseRejectionEvent) => {
        try {
            const reason = (ev as any).reason;
            const msg: string = reason && typeof reason === 'object'
                ? (reason.message ?? String(reason))
                : String(reason ?? '');
            if (isLeafletNoise(msg)) {
                ev.preventDefault();
                ev.stopImmediatePropagation();
                (ev as any).stopPropagation?.();
            }
        } catch { /* noop */ }
    }, true);

    // (c) console.error / console.warn filter (DevTools last-resort drop)
    try {
        const installFilter = (level: 'error' | 'warn' | 'log' | 'info' | 'debug') => {
            const orig = (console as any)[level].bind(console);
            (console as any)[level] = function (this: Console, ...args: any[]) {
                try {
                    const joined = args.map(a => {
                        try {
                            if (!a) return '';
                            if (typeof a === 'string') return a;
                            if (a instanceof Error && LATLNG_SILENT_RE.test(a.message || '')) return '__MUTED__';
                            if (a.message && LATLNG_SILENT_RE.test(a.message)) return '__MUTED__';
                            if (a.stack && LATLNG_SILENT_RE.test(a.stack)) return '__MUTED__';
                            return a.message ?? a.stack ?? String(a);
                        } catch { return ''; }
                    }).join('\n');
                    if (joined.includes('__MUTED__')) return;
                    if (LATLNG_SILENT_RE.test(joined)) return;
                } catch { /* swallow */ }
                return orig(...args);
            };
        };
        installFilter('error');
        installFilter('warn');
    } catch { /* noop */ }

    window.__L_LATLNG_ONERROR_INSTALLED__ = true;
}

// Combined installer — call this at the very top of main.tsx so patches run
// BEFORE any vendor chunk / react-leaflet imports cache the original ctor.
export function installLeafletLatLngPatches(): void {
    installLeafletSafeLatLngPatches();
    installLeafletInvalidLatLngGlobalErrorSuppressor();
}
